import { Alert, Box, Checkbox, CircularProgress, FormControlLabel, FormGroup, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getSlides } from 'api/slides';
import { getSlidesDataForArtifactsResults, runTileGeneration, RunTileGenerationPayload } from 'api/study';
import { useTrackActionFormDirty } from 'components/ActionDialog/actionFormDirtyState';
import { ActionStepper } from 'components/StudyDashboard/ProceduresPage/Actions/ActionStepper';
import {
  getPendingSlidesBaseParams,
  getPendingSlidesParamsString,
  usePendingSlideIdsFromSearchTerm,
} from 'components/StudyDashboard/ProceduresPage/usePendingSlides';
import { JobsTab, useStudyDashboardTabState } from 'components/StudyDashboard/useStudyDashboardTabState';
import { isEqual } from 'es-toolkit';
import { TileGenerationExecuteConfig, TileGenerationJob } from 'interfaces/job';
import { Permission } from 'interfaces/permissionOption';
import { getIsMultiStain } from 'interfaces/stainType';
import { map, pickBy, size, some, uniq, values } from 'lodash';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import React, { ChangeEvent, ReactElement, useEffect, useMemo, useState } from 'react';
import { BooleanParam, StringParam, useQueryParam, useQueryParams } from 'use-query-params';
import { useJob } from 'utils/queryHooks/jobs/useJob';
import { casesSchema } from 'utils/useCasesParams/constants';
import { CasesParams } from 'utils/useCasesParams/types';
import { useMutationWithErrorSnackbar } from 'utils/useMutationWithErrorSnackbar';
import { usePermissions } from 'utils/usePermissions';

const WAITING_SNACKBAR_KEY = 'RUN_TILE_GENERATION';

const DEFAULT_CONFIG_PARAMS: TileGenerationExecuteConfig = {
  skipTiles: false,
  skipMacro: false,
  skipLabel: false,
  skipChannelHistogram: false,
  skipLevelCheck: false,
  skipCreateTiledChannels: false,
  forceRecoverMultiplexMetadata: false,
};

const MULTIPLEX_ONLY_CONFIG_PARAMS: Array<keyof TileGenerationExecuteConfig> = [
  'skipChannelHistogram',
  'skipCreateTiledChannels',
  'forceRecoverMultiplexMetadata',
];

interface TileGenerationSelectionSlide {
  id?: string;
  slideId?: string;
  stainingType?: string;
}

type UnknownRecord = Record<string, unknown>;

const formatSlideCount = (slideCount: number) => `${slideCount} slide${slideCount === 1 ? '' : 's'}`;

export interface RunTileGenerationJobProps {
  onClose: (event?: any, force?: boolean) => void;
  casesParams: CasesParams;
  jobId?: string;
}

const normalizeSlideIds = (slideIds: unknown): string[] | undefined => {
  if (!Array.isArray(slideIds)) {
    return undefined;
  }

  const normalizedSlideIds = slideIds.filter((slideId): slideId is string => typeof slideId === 'string');

  return normalizedSlideIds.length > 0 ? normalizedSlideIds : undefined;
};

const isRecord = (value: unknown): value is UnknownRecord => typeof value === 'object' && value !== null;

const getRawRebuildConfigParams = (job?: TileGenerationJob): UnknownRecord | undefined => {
  if (!isRecord(job?.params)) {
    return undefined;
  }

  const rawConfigParams = job.params.configParams ?? job.params.config_params;

  return isRecord(rawConfigParams) ? rawConfigParams : undefined;
};

const getRebuildConfigParams = (job?: TileGenerationJob): TileGenerationExecuteConfig => ({
  ...DEFAULT_CONFIG_PARAMS,
  ...(getRawRebuildConfigParams(job) || {}),
});

const getRebuildHasAnyMultiplexSlide = (job?: TileGenerationJob): boolean => {
  const rawConfigParams = getRawRebuildConfigParams(job);

  if (typeof rawConfigParams?.multiplex === 'boolean') {
    return rawConfigParams.multiplex;
  }

  if (Array.isArray(job?.children)) {
    return some(job.children, (child) => {
      const childRecord = isRecord(child as unknown) ? (child as UnknownRecord) : undefined;
      const childParams = childRecord && isRecord(childRecord.params) ? childRecord.params : undefined;
      const childResults = childRecord && isRecord(childRecord.results) ? childRecord.results : undefined;

      return childParams?.multiplex === true || childResults?.multiplex === true;
    });
  }

  if (!isRecord(job?.params)) {
    return false;
  }

  const rawProcedureParams = job.params.procedureParams ?? job.params.procedure_params;

  if (!isRecord(rawProcedureParams)) {
    return false;
  }

  const stainingType = rawProcedureParams.stainingType ?? rawProcedureParams.staining_type;

  if (typeof stainingType === 'string') {
    return getIsMultiStain(stainingType);
  }

  if (Array.isArray(stainingType)) {
    return some(stainingType, (value) => typeof value === 'string' && getIsMultiStain(value));
  }

  return false;
};

export const RunTileGenerationJob = ({ onClose, casesParams, jobId }: RunTileGenerationJobProps): ReactElement => {
  const { hasPermission } = usePermissions();
  const canUseExperimentalFeatures = hasPermission(Permission.UseNucleaiExperimentalFeatures);
  const canViewJobs = hasPermission(Permission.ViewJobs);
  const [activeStep, setActiveStep] = useState(0);
  const [isStepFailed, setIsStepFailed] = useState<Record<number, boolean>>({});
  const [configParams, setConfigParams] = useState<TileGenerationExecuteConfig>(DEFAULT_CONFIG_PARAMS);
  const [, setStudyDashboardTab] = useStudyDashboardTabState();
  const [pendingSlidesModeFromQuery] = useQueryParam('pendingSlidesMode', BooleanParam);
  const [, setJobDrawerQuery] = useQueryParams({
    jobDrawerOpen: BooleanParam,
    currentJobId: StringParam,
  });
  const {
    data: rebuildJobData,
    isLoading: isLoadingRebuildJob,
    isError: isErrorRebuildJob,
  } = useJob<TileGenerationJob>(jobId || '', {
    enabled: Boolean(jobId),
  });

  const rebuildManifest = useMemo(() => normalizeSlideIds(rebuildJobData?.manifest), [rebuildJobData]);
  const pendingSlidesMode = jobId ? false : pendingSlidesModeFromQuery;
  const rebuildConfigParams = useMemo(() => getRebuildConfigParams(rebuildJobData), [rebuildJobData]);

  useTrackActionFormDirty(some(values(configParams), Boolean));

  useEffect(() => {
    if (!jobId) {
      setConfigParams((current) => (isEqual(current, DEFAULT_CONFIG_PARAMS) ? current : DEFAULT_CONFIG_PARAMS));
      return;
    }

    if (rebuildJobData) {
      setConfigParams((current) => {
        return isEqual(current, rebuildConfigParams) ? current : rebuildConfigParams;
      });
    }
  }, [jobId, rebuildConfigParams, rebuildJobData]);

  const studyId = casesParams.filters?.studyId || casesParams.studyId;
  const pendingSlidesSearchTerm =
    typeof casesParams.filters?.searchTerm === 'string' ? casesParams.filters.searchTerm : undefined;
  const { slideIdsToInclude: pendingSlideIdsToInclude, isResolvingSearchTermSlideIds } =
    usePendingSlideIdsFromSearchTerm({
      enabled: !jobId && Boolean(studyId) && Boolean(pendingSlidesMode),
      searchTerm: pendingSlidesSearchTerm,
      slideIdsToInclude: casesParams.slideIdsToInclude,
    });
  const pendingSlidesQueryParams = useMemo(() => {
    if (!pendingSlidesMode) {
      return undefined;
    }

    return getPendingSlidesParamsString({
      queryParamsSchema: casesSchema,
      params: {
        ...getPendingSlidesBaseParams({
          filters: casesParams.filters,
          labId: casesParams.labId || '',
          searchTerm: pendingSlidesSearchTerm,
          slideIdsToInclude: pendingSlideIdsToInclude,
        }),
        skipPagination: true,
      },
    });
  }, [casesParams.filters, casesParams.labId, pendingSlideIdsToInclude, pendingSlidesMode, pendingSlidesSearchTerm]);
  const pendingSlidesExecutionCasesParams = useMemo(() => {
    if (!pendingSlidesMode) {
      return undefined;
    }

    return {
      ...casesParams,
      ...getPendingSlidesBaseParams({
        filters: casesParams.filters,
        labId: casesParams.labId || '',
        searchTerm: pendingSlidesSearchTerm,
        slideIdsToInclude: normalizeSlideIds(pendingSlideIdsToInclude),
      }),
      slideIdsToInclude: normalizeSlideIds(pendingSlideIdsToInclude),
    } as CasesParams;
  }, [casesParams, pendingSlideIdsToInclude, pendingSlidesMode, pendingSlidesSearchTerm]);
  const shouldQuerySelection = !jobId;
  const slidesQuery = useQuery<TileGenerationSelectionSlide[]>({
    queryKey: pendingSlidesMode
      ? ['tileGenerationSelectionSlides', 'pending', pendingSlidesQueryParams]
      : ['tileGenerationSelectionSlides', 'procedure-results', casesParams],
    queryFn: ({ signal }) =>
      pendingSlidesMode && pendingSlidesQueryParams
        ? getSlides({ queryParams: pendingSlidesQueryParams, pending: true }, signal)
        : getSlidesDataForArtifactsResults(casesParams, signal),
    enabled:
      shouldQuerySelection &&
      Boolean(studyId) &&
      (!pendingSlidesMode || !isResolvingSearchTermSlideIds) &&
      (!jobId || (!isLoadingRebuildJob && Boolean(rebuildJobData || isErrorRebuildJob))),
  });

  const resolvedSlides = slidesQuery.data || [];
  const resolvedSelectionManifest = useMemo(
    () => rebuildManifest || uniq(map(resolvedSlides, pendingSlidesMode ? 'id' : 'slideId')),
    [pendingSlidesMode, rebuildManifest, resolvedSlides]
  );
  const resolvedSlideCount = size(resolvedSelectionManifest);
  const hasAnyMultiplexSlide = useMemo(
    () =>
      rebuildJobData
        ? getRebuildHasAnyMultiplexSlide(rebuildJobData)
        : some(resolvedSlides, (slide) => Boolean(slide.stainingType && getIsMultiStain(slide.stainingType))),
    [rebuildJobData, resolvedSlides]
  );
  const isResolvingSelection =
    !jobId && Boolean(studyId) && (isResolvingSearchTermSlideIds || slidesQuery.isLoading || slidesQuery.isFetching);

  const selectionErrorMessage = useMemo(() => {
    if (!studyId) {
      return 'A study selection is required before launching tile generation.';
    }

    if (!jobId && slidesQuery.isError) {
      return 'Could not resolve the current slide selection.';
    }

    if (!isResolvingSelection && resolvedSlideCount === 0) {
      return 'No slides matched the current selection.';
    }

    return undefined;
  }, [isResolvingSelection, resolvedSlideCount, slidesQuery.isError, studyId]);

  useEffect(() => {
    if (isResolvingSelection) {
      setIsStepFailed((current) => ({ ...current, 0: false }));
      return;
    }

    setIsStepFailed((current) => ({
      ...current,
      0: Boolean(selectionErrorMessage),
    }));
  }, [isResolvingSelection, selectionErrorMessage]);

  useEffect(() => {
    if (isResolvingSelection || !slidesQuery.isSuccess || hasAnyMultiplexSlide) {
      return;
    }

    setConfigParams((current) => {
      if (!some(MULTIPLEX_ONLY_CONFIG_PARAMS, (field) => current[field])) {
        return current;
      }

      return {
        ...current,
        skipChannelHistogram: false,
        skipCreateTiledChannels: false,
        forceRecoverMultiplexMetadata: false,
      };
    });
  }, [hasAnyMultiplexSlide, isResolvingSelection, slidesQuery.isSuccess]);

  const enabledConfigParams = useMemo(
    () =>
      pickBy(
        {
          skipTiles: configParams.skipTiles,
          skipMacro: configParams.skipMacro,
          skipLabel: configParams.skipLabel,
          skipLevelCheck: configParams.skipLevelCheck,
          ...(hasAnyMultiplexSlide
            ? {
                skipChannelHistogram: configParams.skipChannelHistogram,
                skipCreateTiledChannels: configParams.skipCreateTiledChannels,
                forceRecoverMultiplexMetadata: configParams.forceRecoverMultiplexMetadata,
              }
            : {}),
        },
        Boolean
      ) as TileGenerationExecuteConfig,
    [configParams, hasAnyMultiplexSlide]
  );

  const tileGenerationMutation = useMutationWithErrorSnackbar({
    mutationFn: runTileGeneration,
    mutationDescription: 'launch tile generation',
    onSuccess: ({ jobId: startedJobId }) => {
      enqueueSnackbar(`Tile generation started. Job ID: ${startedJobId}`, { variant: 'success' });

      if (canViewJobs) {
        setStudyDashboardTab(JobsTab);
        setJobDrawerQuery({ jobDrawerOpen: true, currentJobId: startedJobId }, 'replaceIn');
      }

      onClose(undefined, true);
    },
  });

  const checkValidationAndSetIsStepFailed = async (stepIndex: number) => {
    const isValid = stepIndex !== 0 || (!isResolvingSelection && !selectionErrorMessage);

    setIsStepFailed((current) => ({
      ...current,
      [stepIndex]: !isValid,
    }));

    return isValid;
  };

  const updateConfigParam =
    (field: keyof TileGenerationExecuteConfig) => (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setConfigParams((current) => ({
        ...current,
        [field]: checked,
      }));
    };

  const handleSubmit = async () => {
    if (!studyId || isResolvingSelection || selectionErrorMessage) {
      return;
    }

    enqueueSnackbar('Waiting for tile generation to start', {
      key: WAITING_SNACKBAR_KEY,
      autoHideDuration: null,
      variant: 'success',
    });

    try {
      const submissionPayload: RunTileGenerationPayload =
        jobId && rebuildManifest
          ? {
              studyId,
              labId: casesParams.labId,
              manifest: rebuildManifest,
              configParams: enabledConfigParams,
            }
          : {
              ...((pendingSlidesExecutionCasesParams || casesParams) as CasesParams),
              studyId,
              ...(pendingSlidesMode ? { pendingSlidesMode: true } : {}),
              configParams: enabledConfigParams,
            };

      await tileGenerationMutation.mutateAsync(submissionPayload);
    } finally {
      closeSnackbar(WAITING_SNACKBAR_KEY);
    }
  };

  const reviewSelectionStep = {
    label: 'Review selection',
    isLoading: isResolvingSelection,
    subLabel: isResolvingSelection ? 'Resolving slides...' : `Resolved ${formatSlideCount(resolvedSlideCount)}`,
    subDescription: canUseExperimentalFeatures
      ? 'Execute now to use the recommended settings, or continue to Advanced settings for optional troubleshooting controls.'
      : undefined,
    content: (
      <Box>
        {selectionErrorMessage ? (
          <Alert severity={slidesQuery.isError || !studyId ? 'error' : 'warning'}>{selectionErrorMessage}</Alert>
        ) : (
          <>
            <Typography>{`This run will launch tile generation for ${formatSlideCount(
              resolvedSlideCount
            )}.`}</Typography>
            <Typography color="text.secondary" sx={{ mt: 1.5 }}>
              Tile generation will use the appropriate settings for each selected slide.
            </Typography>
            {canUseExperimentalFeatures && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Advanced settings are optional and are mainly useful for troubleshooting or rerunning part of the
                output.
              </Alert>
            )}
          </>
        )}
      </Box>
    ),
    onNextOrBackClick: () => checkValidationAndSetIsStepFailed(0),
  };

  const advancedSettingsStep = {
    label: 'Advanced settings',
    optional: true,
    subDescription: 'Use these settings only for troubleshooting or partial reruns.',
    content: (
      <Box>
        <Alert severity="warning" sx={{ mb: 2 }}>
          These settings only skip parts of processing and are intended for troubleshooting or partial reruns.
        </Alert>
        <FormGroup>
          <FormControlLabel
            control={<Checkbox checked={Boolean(configParams.skipTiles)} onChange={updateConfigParam('skipTiles')} />}
            label="Skip tile pyramid generation"
          />
          <FormControlLabel
            control={<Checkbox checked={Boolean(configParams.skipMacro)} onChange={updateConfigParam('skipMacro')} />}
            label="Skip macro image generation"
          />
          <FormControlLabel
            control={<Checkbox checked={Boolean(configParams.skipLabel)} onChange={updateConfigParam('skipLabel')} />}
            label="Skip label image generation"
          />
          <FormControlLabel
            control={
              <Checkbox checked={Boolean(configParams.skipLevelCheck)} onChange={updateConfigParam('skipLevelCheck')} />
            }
            label="Skip level-count validation"
          />
          {hasAnyMultiplexSlide && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(configParams.skipChannelHistogram)}
                  onChange={updateConfigParam('skipChannelHistogram')}
                />
              }
              label="Skip multiplex channel histograms"
            />
          )}
          {hasAnyMultiplexSlide && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(configParams.skipCreateTiledChannels)}
                  onChange={updateConfigParam('skipCreateTiledChannels')}
                />
              }
              label="Skip multiplex tiled-channel generation"
            />
          )}
          {hasAnyMultiplexSlide && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(configParams.forceRecoverMultiplexMetadata)}
                  onChange={updateConfigParam('forceRecoverMultiplexMetadata')}
                />
              }
              label="Overwrite multiplex channel metadata from TIFF"
            />
          )}
        </FormGroup>
      </Box>
    ),
    onNextOrBackClick: () => checkValidationAndSetIsStepFailed(1),
  };

  const steps = canUseExperimentalFeatures ? [reviewSelectionStep, advancedSettingsStep] : [reviewSelectionStep];

  if (jobId && isLoadingRebuildJob) {
    return <CircularProgress />;
  }

  if (jobId && isErrorRebuildJob) {
    return (
      <Typography>
        Error loading job data. You can close this dialog and try rerunning the tile generation job again.
      </Typography>
    );
  }

  return (
    <ActionStepper
      activeStateHandlers={[activeStep, setActiveStep]}
      handleSubmit={handleSubmit}
      steps={steps}
      setActiveStepForValidation={setActiveStep}
      isStepFailed={isStepFailed}
      validateStep={() => checkValidationAndSetIsStepFailed(activeStep)}
    />
  );
};
